import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface AdminSchool {
  id: string
  name: string
  mtoLicenceNumber: string | null
  city: string | null
  maxInstructorSeats: number | null
  activeInstructorCount: number
  accountStatus: 'active' | 'suspended' | 'pending_verification'
}

export interface AdminProfile {
  id: string
  email: string
  fullName: string | null
  primaryRole: string | null
  accountStatus: 'active' | 'suspended' | 'pending_verification'
  isSuperadmin: boolean
  createdAt: string
}

/** drivetrack_admin's platform directory — schools (with seat usage, not real billing:
 * no payment processor exists anywhere in this codebase) and every profile, readable via
 * the new "drivetrack admin read" policies (migration 066). Suspend/activate goes through
 * admin-set-account-status (service-role Edge Function) — RLS hard-blocks a client JWT
 * from writing account_status under any role, by design (migration 026). */
export function useAdminSchools() {
  const [schools, setSchools] = useState<AdminSchool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSchools = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data: schoolRows, error: schoolErr } = await supabase
      .from('schools')
      .select('id, name, mto_licence_number, city, max_instructor_seats, account_status')
      .order('name', { ascending: true })
    if (schoolErr) { setError('Could not load schools.'); setLoading(false); return }

    const { data: memberRows } = await supabase
      .from('school_memberships')
      .select('school_id, role, status')
      .eq('status', 'active')

    const countBySchool = new Map<string, number>()
    for (const m of memberRows ?? []) {
      if (m.role !== 'INSTRUCTOR') continue
      countBySchool.set(m.school_id, (countBySchool.get(m.school_id) ?? 0) + 1)
    }

    setSchools((schoolRows ?? []).map((r: any) => ({
      id: r.id, name: r.name, mtoLicenceNumber: r.mto_licence_number, city: r.city,
      maxInstructorSeats: r.max_instructor_seats,
      activeInstructorCount: countBySchool.get(r.id) ?? 0,
      accountStatus: r.account_status,
    })))
    setLoading(false)
  }, [])

  useEffect(() => { fetchSchools() }, [fetchSchools])

  return { schools, loading, error, refresh: fetchSchools }
}

export function useAdminProfiles() {
  const [profiles, setProfiles] = useState<AdminProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState<string | null>(null)

  const fetchProfiles = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('profiles')
      .select('id, email, full_name, primary_role, account_status, is_superadmin, created_at')
      .order('created_at', { ascending: false })
    if (fetchErr) { setError('Could not load accounts.'); setLoading(false); return }
    setProfiles((data ?? []).map((r: any) => ({
      id: r.id, email: r.email, fullName: r.full_name, primaryRole: r.primary_role,
      accountStatus: r.account_status, isSuperadmin: r.is_superadmin, createdAt: r.created_at,
    })))
    setLoading(false)
  }, [])

  useEffect(() => { fetchProfiles() }, [fetchProfiles])

  const setAccountStatus = useCallback(async (profileId: string, status: 'active' | 'suspended') => {
    setUpdating(profileId)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('admin-set-account-status', {
        body: { profileId, status },
      })
      if (fnErr || data?.error) return { ok: false, error: data?.error ?? 'Could not update account status.' }
      await fetchProfiles()
      return { ok: true }
    } finally {
      setUpdating(null)
    }
  }, [fetchProfiles])

  const setSuperadminTier = useCallback(async (profileId: string, isSuperadmin: boolean) => {
    setUpdating(profileId)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('admin-set-superadmin-tier', {
        body: { profileId, isSuperadmin },
      })
      if (fnErr || data?.error) return { ok: false, error: data?.error ?? 'Could not update superadmin tier.' }
      await fetchProfiles()
      return { ok: true }
    } finally {
      setUpdating(null)
    }
  }, [fetchProfiles])

  return { profiles, loading, error, updating, refresh: fetchProfiles, setAccountStatus, setSuperadminTier }
}
